<template>
  <div
    v-if="isOpen"
    ref="modalRef"
    @click.stop
    class="absolute right-0 z-20 mt-2 w-[615px] origin-top-right rounded-lg bg-white shadow-xl border border-gray-200"
  >
    <div class="flex items-center justify-between px-4 pt-4 pb-2">
      <h3 class="text-lg font-bold text-textPrimary">Grupos de colunas:</h3>
      <button @click="closeModal" class="text-gray-400 hover:text-gray-600">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
        >
          <path
            d="M18.295 7.11502C18.6844 6.72565 18.6844 6.09438 18.295 5.70502C17.9056 5.31565 17.2744 5.31565 16.885 5.70502L12 10.59L7.115 5.70502C6.72564 5.31565 6.09436 5.31565 5.705 5.70502C5.31564 6.09438 5.31564 6.72565 5.705 7.11502L10.59 12L5.705 16.885C5.31564 17.2744 5.31564 17.9057 5.705 18.295C6.09436 18.6844 6.72564 18.6844 7.115 18.295L12 13.41L16.885 18.295C17.2744 18.6844 17.9056 18.6844 18.295 18.295C18.6844 17.9057 18.6844 17.2744 18.295 16.885L13.41 12L18.295 7.11502Z"
            fill="#68688D"
          />
        </svg>
      </button>
    </div>

    <div class="px-6 py-3">
      <div class="grid grid-cols-2 gap-4">
        <div v-for="column in internalColumns" :key="column.value">
          <Checkbox
            :fieldId="`col-${column.value}`"
            :label="column.label"
            :value="column.value"
            :checked="column.visible"
            @update:checked="updateColumnVisibility"
            :disabled="column.label === 'Plantão'"
          />
        </div>
      </div>
    </div>

    <div class="flex justify-end p-4">
      <FilledButton
        text="Aplicar alterações"
        color="primary30"
        height="48px"
        width="180px"
        @click="applyChanges"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, watch, onBeforeUnmount, PropType } from 'vue'
import Checkbox from '@/components/designSystem/Checkbox/index.vue'
import FilledButton from '@/components/designSystem/Buttons/FilledButton/index.vue'
export interface ColumnOption {
  label: string
  value: string
  visible: boolean
}

const props = defineProps({
  isOpen: {
    type: Boolean,
    required: true
  },
  columns: {
    type: Array as PropType<ColumnOption[]>,
    required: true
  }
})

const emit = defineEmits<{
  (e: 'update:isOpen', value: boolean): void
  (e: 'apply', updatedColumns: ColumnOption[]): void
}>()

const internalColumns = ref<ColumnOption[]>([])
const modalRef = ref<HTMLElement | null>(null)

const handleClickOutside = (event: MouseEvent) => {
  if (modalRef.value && !modalRef.value.contains(event.target as Node)) {
    closeModal()
  }
}

watch(
  () => props.isOpen,
  (newVal) => {
    if (newVal) {
      internalColumns.value = JSON.parse(JSON.stringify(props.columns))
      document.addEventListener('mousedown', handleClickOutside)
    } else {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }
)

onBeforeUnmount(() => {
  document.removeEventListener('mousedown', handleClickOutside)
})

function updateColumnVisibility({ isChecked, value }: { isChecked: boolean; value: string }) {
  const column = internalColumns.value.find((c) => c.value === value)
  if (column) {
    column.visible = isChecked
  }
}

function closeModal() {
  emit('update:isOpen', false)
}

function applyChanges() {
  emit('apply', internalColumns.value)
  closeModal()
}
</script>

<style scoped>
:deep(input[type='checkbox']) {
  appearance: none;
  -webkit-appearance: none;
  height: 18px;
  width: 18px;
  background-color: white;
  border-radius: 4px;
  border: 2px solid #2f7f91;
  display: flex;
  margin-right: 8px;
  cursor: pointer;
  flex-shrink: 0;
}

:deep(input[type='checkbox']::after) {
  content: ' ';
  height: 100%;
  width: 100%;
  background-color: #2f7f91;
  background-image: url('data:image/svg+xml;base64, PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIGZpbGw9IndoaXRlIiB2aWV3Qm94PSIwIDAgNTEyIDUxMiI+PCEtLSEgRm9udCBBd2Vzb21lIFBybyA2LjIuMCBieSBAZm9udGF3ZXNvbWUgLSBodHRwczovL2ZvbnRhd2Vzb21lLmNvbSBMaWNlbnNlIC0gaHR0cHM6Ly9mb250YXdlc29tZS5jb20vbGljZW5zZSAoQ29tbWVyY2lhbCBMaWNlbnNlKSBDb3B5cmlnaHQgMjAyMiBGb250aWNvbnMsIEluYy4gLS0+PHBhdGggZD0iTTQ3MC42IDEwNS40YzEyLjUgMTIuNSAxMi41IDMyLjggMCA0NS4zbC0yNTYgMjU2Yy0xMi41IDEyLjUtMzIuOCAxMi41LTQ1LjMgMGwtMTI4LTEyOGMtMTIuNS0xMi41LTEyLjUtMzIuOCAwLTQ1LjNzMzIuOC0xMi41IDQ1LjMgMEwxOTIgMzM4LjcgNDI1LjQgMTA1LjRjMTIuNS0xMi41IDMyLjgtMTIuNSA0NS4zIDB6Ii8+PC9zdmc+');
  background-size: contain;
  display: none;
}

:deep(input[type='checkbox']:checked:after) {
  display: block;
}

:deep(label) {
  cursor: pointer;
  word-break: normal;
  user-select: none;
}
</style>
